import React, { useState } from 'react';
import { Button, Alert, Modal, Spinner, RowActions, StatusBadge, DataTable, type DataTableColumn } from '@/components/ui';
import { Invoice } from '../../../types/Invoice';
import { format } from 'date-fns';
import { formatDollars } from '../../../utils/formatDollars';
import { parseCalendarDate } from '../../../utils/dateUtils';
import { useClientSort } from '../../../utils/useClientSort';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';

const SORT_VALUES: Record<string, (inv: Invoice) => string | number | null | undefined> = {
  invoiceDate: (inv) => (inv.invoiceDate ? new Date(inv.invoiceDate).getTime() : null),
  invoiceValue: (inv) => inv.invoiceValue,
  invoiceNumber: (inv) => inv.invoiceNumber,
  status: (inv) => inv.status,
};

interface InvoiceListProps {
  invoices: Invoice[];
  onEdit: (invoice: Invoice) => void;
  onDelete: (invoiceId: number) => Promise<void>;
  onViewAttachment: (attachment: any, invoice: Invoice) => void;
  onRetryUpload: (invoiceId: number, attachmentId: number) => Promise<void>;
  loading?: boolean;
}

const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  onEdit,
  onDelete,
  onViewAttachment,
  onRetryUpload,
  loading = false
}) => {
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);
  // A failed upload has no file to open; say so next to the list instead of a browser alert.
  const [uploadNotice, setUploadNotice] = useState<string | null>(null);
  const { sorted, sortBy, sortDir, onSort } = useClientSort(invoices, SORT_VALUES);

  const handleDeleteClick = (invoice: Invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) return;
    
    setDeletingInvoiceId(invoiceToDelete.id);
    try {
      await onDelete(invoiceToDelete.id);
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
    } finally {
      setDeletingInvoiceId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setInvoiceToDelete(null);
  };

  const handleViewAttachment = (attachment: any, invoice: Invoice) => {
    // Check if this is a placeholder URL (failed upload)
    if (attachment.attachmentURL && attachment.attachmentURL.startsWith('placeholder://')) {
      setUploadNotice(`"${attachment.attachmentName}" didn't upload, so it can't be opened. Upload the file again.`);
      return;
    }
    
    // Handle normal attachment viewing - pass both attachment and invoice
    onViewAttachment(attachment, invoice);
  };

  const formatDate = (date: string | Date) => {
    try {
      // invoiceDate is a calendar date (no meaningful time-of-day), but the API returns it as
      // a UTC-midnight ISO string. Parsing that with `new Date(str)` and formatting with
      // date-fns (which reads local getters) shows the *previous* day in any timezone behind
      // UTC — parseCalendarDate anchors it at local midnight for the same calendar day instead.
      // V2-073: the shared formatter keeps a calendar date on its own day.
      return formatPacificDate(date) || 'Invalid date';
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="tw-text-center tw-p-4">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <Alert variant="info" className="tw-text-center">
        No invoices found for this project. Click "Add Invoice" to create the first one.
      </Alert>
    );
  }

  // Invoice # is the identifying label (a card has no click-through, unlike the project list),
  // so it's the title; Date is the secondary line. Status + the row menu sit top-right.
  const columns: DataTableColumn<Invoice>[] = [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      sortable: true,
      mobile: 'title',
      cell: (invoice) => invoice.invoiceNumber || '-',
    },
    {
      key: 'invoiceDate',
      header: 'Date',
      sortable: true,
      mobile: 'subtitle',
      cell: (invoice) => formatDate(invoice.invoiceDate),
    },
    {
      key: 'invoiceValue',
      header: 'Value',
      sortable: true,
      className: 'tw-font-bold',
      cell: (invoice) => formatDollars(invoice.invoiceValue),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      cell: (invoice) => <StatusBadge status={invoice.status} vocabulary="invoice" />,
    },
    {
      key: 'attachments',
      header: 'Attachments',
      cell: (invoice) =>
        invoice.attachments && invoice.attachments.length > 0 ? (
          <div className="tw-flex tw-flex-wrap tw-gap-1">
            {invoice.attachments.map((attachment) => {
              const isPlaceholder = attachment.attachmentURL && attachment.attachmentURL.startsWith('placeholder://');
              return (
                <div key={attachment.id} className="tw-flex tw-gap-1">
                  <Button
                    size="sm"
                    variant={isPlaceholder ? "outline-warning" : "outline-primary"}
                    onClick={() => handleViewAttachment(attachment, invoice)}
                    title={isPlaceholder ?
                      `Upload failed: ${attachment.attachmentName}` :
                      (attachment.attachmentNotes || attachment.attachmentName)
                    }
                  >
                    <i className={`bi ${isPlaceholder ? 'bi-exclamation-triangle' : 'bi-file-pdf'} tw-mr-1`}></i>
                    {isPlaceholder ? 'Failed' : 'PDF'}
                  </Button>
                  {isPlaceholder && (
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => onRetryUpload(invoice.id, attachment.id)}
                      title="Retry upload"
                    >
                      <i className="bi bi-arrow-clockwise"></i>
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <span className="tw-text-content-muted">No attachments</span>
        ),
    },
    {
      key: 'comments',
      header: 'Comments',
      cell: (invoice) =>
        invoice.comments ? (
          <span title={invoice.comments} className="tw-truncate tw-inline-block" style={{ maxWidth: '150px' }}>
            {invoice.comments}
          </span>
        ) : (
          <span className="tw-text-content-muted">-</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      cell: (invoice) => (
        <RowActions
          actions={[
            { kind: 'edit', label: 'Edit invoice', onClick: () => onEdit(invoice) },
            {
              kind: 'delete',
              label: 'Delete invoice',
              onClick: () => handleDeleteClick(invoice),
              disabled: deletingInvoiceId === invoice.id,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="tw-w-full">
      {uploadNotice && (
        <Alert variant="warning" dismissible onClose={() => setUploadNotice(null)} className="tw-mb-3">
          {uploadNotice}
        </Alert>
      )}
      <DataTable columns={columns} data={sorted} rowKey={(invoice) => invoice.id} striped hover sortBy={sortBy} sortDir={sortDir} onSort={onSort} />

      <Modal show={showDeleteModal} onHide={handleDeleteCancel}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete invoice #{invoiceToDelete?.invoiceNumber}? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deletingInvoiceId === invoiceToDelete?.id}>
            {deletingInvoiceId === invoiceToDelete?.id ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InvoiceList;
